import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Alert, CircularProgress } from '@mui/material';

import TSButton from 'components/common/Button/Button';
import {
  useGetEmergencyContactWelfareCheckQuery,
  useSubmitEmergencyContactWelfareCheckResponseMutation,
} from 'app/api/welfareCheckResponseApiSlice';

const RESPONSE_OPTIONS = [
  { option: 'CONFIRMED_SAFE', label: "I've reached them — they're fine" },
  { option: 'WILL_TRY', label: "I'll try to reach them" },
  { option: 'CANT_REACH', label: "I can't reach them" },
];

// Trail Safe doesn't auto-dispatch on this flag (same guidance-only stance
// as the walker's "I need help" — see docs/adr/0001); it's shown once the
// Emergency Contact tells us they can't reach the walker.
const CANT_REACH_ADVICE =
  "If you believe this is a genuine emergency, contact emergency services (000) or Tasmania Police directly — Trail Safe does not dispatch rescue services automatically.";

const formatExpectedEndTime = (isoString) =>
  isoString
    ? new Date(isoString).toLocaleString('en-AU', {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '';

// Copy is hardcoded with variable interpolation, not database-driven — see
// docs/adr/0005.
const buildStatusText = ({ state, walkerName, trailName, expectedEndTime }) => {
  switch (state) {
    case 'NOT_DUE':
      return `${walkerName} is on track — no action needed right now.`;
    case 'FIRST_PROMPT_SENT':
    case 'URGENT_PROMPT_SENT':
      return `${walkerName} hasn't checked in yet. They were expected back from ${trailName} by ${expectedEndTime}.`;
    case 'ESCALATED':
      return `${walkerName} hasn't checked in and is overdue from ${trailName} (expected back ${expectedEndTime}). Please try to reach them and let us know what you find.`;
    case 'RESOLVED':
      return `This activity is now closed — ${walkerName} has been marked safe. Nothing further required.`;
    default:
      return '';
  }
};

const ContactResponse = () => {
  const { token } = useParams();
  const { data, isLoading, isError } =
    useGetEmergencyContactWelfareCheckQuery(token);
  const [submitResponse, { isLoading: isSubmitting }] =
    useSubmitEmergencyContactWelfareCheckResponseMutation();

  const [liveState, setLiveState] = useState(null);
  const [lastSubmitted, setLastSubmitted] = useState(null);

  if (isLoading) {
    return (
      <div className="flex justify-center">
        <CircularProgress />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col gap-2">
        <Typography variant="h5" fontWeight={700}>
          This link isn't valid
        </Typography>
        <Typography variant="body1">
          Double check the link from your text message — it may be out of
          date.
        </Typography>
      </div>
    );
  }

  const { walker_name, trail_name, expected_end_date } = data;
  const state = liveState ?? data.state;
  const expectedEndTime = formatExpectedEndTime(expected_end_date);

  const handleRespond = async (option) => {
    const result = await submitResponse({ token, option }).unwrap();
    setLiveState(result.state);
    setLastSubmitted(option);
  };

  return (
    <div className="flex flex-col gap-4">
      <Typography variant="h5" fontWeight={700}>
        Welfare Check for {walker_name}
      </Typography>
      <Typography variant="body1">
        {buildStatusText({
          state,
          walkerName: walker_name,
          trailName: trail_name,
          expectedEndTime,
        })}
      </Typography>

      {state !== 'RESOLVED' && (
        <div className="flex flex-col gap-2">
          {RESPONSE_OPTIONS.map(({ option, label }) => (
            <TSButton
              key={option}
              btnText={label}
              btnVariant={lastSubmitted === option ? 'contained' : 'outlined'}
              onClickFunc={() => handleRespond(option)}
              isLoading={isSubmitting}
            />
          ))}
        </div>
      )}

      {lastSubmitted && (
        <Alert severity="success">
          Got it — thanks, logged as: "
          {RESPONSE_OPTIONS.find((o) => o.option === lastSubmitted)?.label}".
        </Alert>
      )}

      {lastSubmitted === 'CANT_REACH' && (
        <Alert severity="info">{CANT_REACH_ADVICE}</Alert>
      )}
    </div>
  );
};

export default ContactResponse;
